import { useState, useEffect } from 'react';
import { adminConfigAPI } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Key, Plus, Trash2, Loader2, Save, Eye, EyeOff,
  Phone, Mic, Volume2, CreditCard, Globe, Webhook, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const KEY_PRESETS = [
  { key: 'VOBIZ_MASTER_AUTH_ID', label: 'Vobiz Master Auth ID', icon: Phone, category: 'Telephony (Vobiz)', desc: 'Vobiz cloud telephony master account Auth ID. Get from https://app.vobiz.in/' },
  { key: 'VOBIZ_MASTER_AUTH_TOKEN', label: 'Vobiz Master Auth Token', icon: Phone, category: 'Telephony (Vobiz)', desc: 'Vobiz master account Auth Token for API access' },
  { key: 'VOBIZ_MASTER_PHONE_NUMBER', label: 'Vobiz Platform Number', icon: Phone, category: 'Telephony (Vobiz)', desc: 'Shared platform number for all users (outbound). Format: +917971542831' },
  { key: 'XAI_API_KEY', label: 'xAI (Grok) API Key', icon: Mic, category: 'Voice AI (Grok)', desc: 'xAI Grok Voice Agent API key — powers ALL voice calls (Free Demo, Standard, Premium, Enterprise). Get from https://console.x.ai/' },
  { key: 'OPENAI_API_KEY', label: 'OpenAI API Key', icon: Mic, category: 'Non-voice AI', desc: 'OpenAI API key for text-only features (DotCall chatbot widget, AI Fill, Knowledge Base generation). Not used for voice calls.' },
  { key: 'GOOGLE_CLIENT_ID', label: 'Google OAuth Client ID', icon: Globe, category: 'Authentication', desc: 'Google OAuth 2.0 Client ID for social login (from Google Cloud Console)' },
  { key: 'GOOGLE_CLIENT_SECRET', label: 'Google OAuth Client Secret', icon: Shield, category: 'Authentication', desc: 'Google OAuth 2.0 Client Secret' },
  { key: 'RAZORPAY_KEY_ID', label: 'Razorpay Key ID', icon: CreditCard, category: 'Billing', desc: 'Razorpay key ID for payments' },
  { key: 'RAZORPAY_KEY_SECRET', label: 'Razorpay Key Secret', icon: CreditCard, category: 'Billing', desc: 'Razorpay key secret' },
  { key: 'HUBSPOT_API_KEY', label: 'HubSpot API Key', icon: Globe, category: 'Integrations (CRM)', desc: 'Connect to HubSpot CRM — sync call data, contacts, and deal updates automatically after AI calls' },
  { key: 'ZAPIER_WEBHOOK_URL', label: 'Zapier Webhook URL', icon: Webhook, category: 'Integrations (CRM)', desc: 'Zapier webhook endpoint — trigger automated workflows after calls (e.g., update spreadsheets, send emails, notify Slack)' },
  { key: 'GOOGLE_SHEETS_KEY', label: 'Google Sheets API Key', icon: Globe, category: 'Integrations (CRM)', desc: 'Google Sheets integration — auto-log call results, transcripts, and campaign data to a Google Sheet' },
  { key: 'OPENROUTER_API_KEY', label: 'OpenRouter API Key', icon: Key, category: 'AI Blog & SEO', desc: 'Powers AI auto-blogger (Claude Sonnet 4.5 for text + Nano Banana for featured images). One key for both. Required for blog generation. Get from https://openrouter.ai/keys (starts with sk-or-v1-...).' },
];

export default function AdminApiKeysPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showValues, setShowValues] = useState({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showAddCustom, setShowAddCustom] = useState(false);

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      const res = await adminConfigAPI.list();
      setConfigs(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getConfigValue = (key) => configs.find(c => c.key === key)?.value || '';

  const handleSave = async (key, value) => {
    if (!value.trim()) { toast.error('Value cannot be empty'); return; }
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await adminConfigAPI.set(key, value);
      toast.success(`${key} saved`);
      loadConfigs();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDelete = async (key) => {
    try {
      await adminConfigAPI.delete(key);
      toast.success(`${key} removed`);
      loadConfigs();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleAddCustom = async () => {
    if (!newKey.trim() || !newValue.trim()) { toast.error('Key and value are required'); return; }
    await handleSave(newKey, newValue);
    setNewKey('');
    setNewValue('');
    setShowAddCustom(false);
  };

  const toggleShowValue = (key) => setShowValues(prev => ({ ...prev, [key]: !prev[key] }));

  const categories = [...new Set(KEY_PRESETS.map(k => k.category))];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-8 max-w-5xl" data-testid="admin-api-keys-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">API Keys & Configuration</h1>
          <p className="text-slate-400 mt-1">Manage platform integrations and service keys</p>
        </div>
        <Button onClick={() => setShowAddCustom(!showAddCustom)} variant="outline"
          className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="add-custom-key-button">
          <Plus className="w-4 h-4 mr-2" /> Custom Key
        </Button>
      </div>

      {/* Status Banner */}
      <div className="glass-card rounded-xl p-4 flex items-center gap-3">
        <Shield className="w-5 h-5 text-amber-400" />
        <p className="text-sm text-slate-300">
          Keys are stored securely in your database. Add your live API keys here to enable real integrations.
          <span className="text-amber-400 ml-1">{configs.length} keys configured.</span>
        </p>
      </div>

      {/* Custom Key Input */}
      {showAddCustom && (
        <div className="glass-card rounded-xl p-5 animate-fade-in" data-testid="custom-key-form">
          <h3 className="text-white font-medium mb-3">Add Custom Key</h3>
          <div className="flex gap-3">
            <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="KEY_NAME"
              className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 font-mono" data-testid="custom-key-name" />
            <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="value"
              className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 flex-1" data-testid="custom-key-value" />
            <Button onClick={handleAddCustom} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-custom-key">
              <Save className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Preset Keys by Category */}
      {categories.map(category => (
        <div key={category} className="space-y-3">
          <h2 className="text-lg font-semibold font-['Outfit'] text-white flex items-center gap-2">
            {category}
            <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-xs">
              {KEY_PRESETS.filter(k => k.category === category && getConfigValue(k.key)).length}/{KEY_PRESETS.filter(k => k.category === category).length}
            </Badge>
          </h2>
          <div className="space-y-2">
            {KEY_PRESETS.filter(k => k.category === category).map((preset) => {
              const currentValue = getConfigValue(preset.key);
              const isConfigured = !!currentValue;
              return (
                <KeyRow
                  key={preset.key}
                  preset={preset}
                  currentValue={currentValue}
                  isConfigured={isConfigured}
                  showValue={showValues[preset.key]}
                  toggleShowValue={() => toggleShowValue(preset.key)}
                  onSave={(value) => handleSave(preset.key, value)}
                  onDelete={() => handleDelete(preset.key)}
                  saving={saving[preset.key]}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Custom Configs */}
      {configs.filter(c => !KEY_PRESETS.find(p => p.key === c.key)).length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold font-['Outfit'] text-white">Custom Keys</h2>
          <div className="space-y-2">
            {configs.filter(c => !KEY_PRESETS.find(p => p.key === c.key)).map((config) => (
              <div key={config.key} className="glass-card rounded-lg p-4 flex items-center gap-4" data-testid={`custom-config-${config.key}`}>
                <Key className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-mono">{config.key}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {showValues[config.key] ? config.value : '••••••••••••'}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => toggleShowValue(config.key)} className="text-slate-400 hover:text-white">
                  {showValues[config.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(config.key)} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KeyRow({ preset, currentValue, isConfigured, showValue, toggleShowValue, onSave, onDelete, saving }) {
  const [value, setValue] = useState(currentValue);
  const [editing, setEditing] = useState(false);

  useEffect(() => { setValue(currentValue); }, [currentValue]);

  return (
    <div className={`glass-card rounded-lg p-4 transition-all ${isConfigured ? 'border-emerald-600/20' : ''}`}
      data-testid={`api-key-${preset.key}`}>
      <div className="flex items-start gap-4">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isConfigured ? 'bg-emerald-600/20 text-emerald-400' : 'bg-slate-800 text-slate-500'
        }`}>
          <preset.icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm text-white font-medium">{preset.label}</span>
            {isConfigured && (
              <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30 text-[10px]">Active</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-2">{preset.desc}</p>
          
          {editing || !isConfigured ? (
            <div className="flex gap-2">
              <Input
                type={showValue ? 'text' : 'password'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={`Enter ${preset.label}`}
                className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 text-sm h-9 font-mono flex-1"
                data-testid={`input-${preset.key}`}
              />
              <Button size="sm" variant="ghost" onClick={toggleShowValue} className="text-slate-400 hover:text-white h-9 w-9 p-0">
                {showValue ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </Button>
              <Button size="sm" onClick={() => { onSave(value); setEditing(false); }} disabled={saving}
                className="bg-amber-600 hover:bg-amber-700 text-white h-9" data-testid={`save-${preset.key}`}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <code className="text-xs text-slate-500 font-mono">
                {showValue ? currentValue : '••••••••' + currentValue.slice(-4)}
              </code>
              <Button size="sm" variant="ghost" onClick={toggleShowValue} className="text-slate-400 hover:text-white h-7 w-7 p-0">
                {showValue ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="text-cyan-400 hover:text-cyan-300 h-7 text-xs">
                Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-400 hover:text-red-300 h-7 w-7 p-0">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
